import { useUpdatePrototypeStatus } from "@faire/prototype-playground/api/prototype";
import {
  Prototype,
  PrototypeStatus,
} from "@faire/prototype-playground/config/prototype-types";
import { useAuth } from "@faire/prototype-playground/contexts/AuthContext";
import { cn } from "@faire/prototype-playground/lib/utils";
import { useCurrentPrototype } from "@faire/prototype-playground/platform/components/toolbar/useCurrentPrototype";
import { isDevelopment } from "@faire/prototype-playground/platform/lib/env";
import { useState } from "react";

export const StatusSelector = () => {
  const currentPrototype = useCurrentPrototype();
  const { user } = useAuth();
  const isReadOnly = isDevelopment() || !user;
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [status, setStatus] = useState<Prototype["status"]>(
    currentPrototype?.status === "legacy"
      ? "in-progress"
      : (currentPrototype?.status ?? "in-progress")
  );

  const protoId = currentPrototype?.id ?? "variant-switcher-v2";
  const activeStatus =
    STATUS_OPTIONS.find((s) => s.value === status) ?? STATUS_OPTIONS[0];

  const { mutate: updateStatus } = useUpdatePrototypeStatus(protoId, {
    onMutate: ({ status: newStatus }) => {
      setStatus(newStatus);
    },
  });

  const onToggleDropdown = () => {
    if (isReadOnly) return;
    setStatusDropdownOpen((p) => !p);
  };

  return (
    <div className="relative">
      <div className="text-[10px] font-medium tracking-wide text-[#aaa] uppercase">
        Status
      </div>
      <button
        onClick={onToggleDropdown}
        disabled={isReadOnly}
        className={cn(
          "mt-0.5 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
          isReadOnly ? "cursor-default" : "hover:brightness-125",
          activeStatus.bg,
          activeStatus.text,
          activeStatus.border
        )}
      >
        {activeStatus.label}
        {isReadOnly ? null : (
          <svg
            className={cn(
              "h-2.5 w-2.5 transition-transform",
              statusDropdownOpen ? "rotate-180" : ""
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </button>
      {statusDropdownOpen && !isReadOnly ? (
        <div className="absolute top-full left-0 z-10 mt-1.5 w-40 overflow-hidden rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                updateStatus({ status: opt.value });
                setStatusDropdownOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors",
                status === opt.value ? "bg-[#2a2a2a]" : "hover:bg-[#222]"
              )}
            >
              <span
                className={cn(
                  "inline-block h-2 w-2 rounded-full border",
                  opt.bg,
                  opt.border
                )}
              />
              <span
                className={cn(
                  "text-[11px]",
                  status === opt.value
                    ? "font-medium text-(--slate-color-action-text-inverse)"
                    : "text-[#999]"
                )}
              >
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const STATUS_OPTIONS: Array<{
  value: PrototypeStatus;
  label: string;
  bg: string;
  text: string;
  border: string;
}> = [
  {
    value: "in-progress",
    label: "In Progress",
    bg: "bg-[#fe9a00]/15",
    text: "text-[#fbbf24]",
    border: "border-[#fe9a00]/30",
  },
  {
    value: "ready-for-dev",
    label: "Ready for Dev",
    bg: "bg-[#2b7fff]/15",
    text: "text-[#60a5fa]",
    border: "border-[#2b7fff]/30",
  },
  {
    value: "shipped",
    label: "Shipped",
    bg: "bg-[#00bc7d]/15",
    text: "text-[#34d399]",
    border: "border-[#00bc7d]/30",
  },
  {
    value: "archived",
    label: "Archived",
    bg: "bg-[#555]/15",
    text: "text-[#888]",
    border: "border-[#555]/30",
  },
] as const;
